import axios from 'axios';
import { toast } from 'react-toastify';

const API = axios.create({
  baseURL: process.env.REACT_APP_API_URL || '/api',
  withCredentials: true,
  timeout: 30000,
});

// Cheap RFC4122-ish UUID for Idempotency-Key headers (no extra dep needed)
export const uuid = () =>
  'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0;
    const v = c === 'x' ? r : (r & 0x3) | 0x8;
    return v.toString(16);
  });

// Read a cookie value (non-HttpOnly only — that's all the browser exposes
// to JS anyway). Used to echo the CSRF token on unsafe methods.
const readCookie = (name) => {
  if (typeof document === 'undefined') return null;
  const m = document.cookie.match(new RegExp('(?:^|; )' + name.replace(/[.*+?^${}()|[\]\\]/g, '\\$&') + '=([^;]*)'));
  return m ? decodeURIComponent(m[1]) : null;
};

// Attach JWT + CSRF token. JWT is for authenticated requests; CSRF is for
// cookie-auth routes the server protects. Adding the header on bearer
// requests is harmless (server skips the check), and we may add cookie-auth
// flows later without revisiting this file.
API.interceptors.request.use((config) => {
  const token = localStorage.getItem('token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  const method = (config.method || 'get').toUpperCase();
  if (!['GET', 'HEAD', 'OPTIONS'].includes(method)) {
    const csrf = readCookie('csrf-token');
    if (csrf) config.headers['X-CSRF-Token'] = csrf;
  }
  return config;
});

// Centralised response handling
let isRedirectingToLogin = false;

API.interceptors.response.use(
  (response) => response,
  (error) => {
    const status = error.response?.status;
    const data = error.response?.data;
    const message = data?.message || error.message || 'Request failed';
    const silent = error.config?.silent === true;

    // Network / timeout (no response)
    if (!error.response) {
      if (!silent) toast.error('Network error — please check your connection');
      return Promise.reject(error);
    }

    // Auth expired / invalid → clear session and bounce to login (without hard reload)
    if (status === 401) {
      localStorage.removeItem('token');
      localStorage.removeItem('user');
      const onAuthPage = ['/login', '/register', '/forgot-password', '/reset-password'].some((p) =>
        window.location.pathname.startsWith(p)
      );
      if (!onAuthPage && !isRedirectingToLogin) {
        isRedirectingToLogin = true;
        const next = encodeURIComponent(window.location.pathname + window.location.search);
        // Soft navigate — preserves SPA state where possible
        window.history.pushState({}, '', `/login?next=${next}`);
        window.dispatchEvent(new PopStateEvent('popstate'));
        setTimeout(() => {
          isRedirectingToLogin = false;
        }, 1000);
      }
      if (!silent) toast.error(message);
      return Promise.reject(error);
    }

    if (status === 403 && data?.code === 'CSRF_INVALID') {
      // Token drift — usually after a long-idle tab. A reload re-issues the
      // CSRF cookie and lets the user retry without seeing a scary error.
      if (!silent) toast.error('Your session expired. Reloading…');
      setTimeout(() => window.location.reload(), 1200);
      return Promise.reject(error);
    }

    if (status === 423) {
      if (!silent) toast.error(message); // account locked
      return Promise.reject(error);
    }

    if (status === 429) {
      // Server sends Retry-After (seconds). Fall back to body.retryAfter,
      // then to a generic "slow down". Surfacing the countdown means users
      // stop panic-clicking.
      const headerVal = error.response.headers?.['retry-after'];
      const headerSec = headerVal ? parseInt(headerVal, 10) : null;
      const retryAfter = Number.isFinite(headerSec) ? headerSec : data?.retryAfter;
      const niceMessage = retryAfter
        ? `Too many requests — try again in ${retryAfter}s`
        : 'Too many requests — please slow down';
      // Attach to error so callers can show inline countdown if they want.
      error.retryAfter = retryAfter;
      if (!silent) toast.error(niceMessage);
      return Promise.reject(error);
    }

    if (status >= 500) {
      if (!silent) toast.error('Server error — please try again shortly');
      return Promise.reject(error);
    }

    // 4xx (validation, business rules)
    if (!silent) toast.error(message);
    return Promise.reject(error);
  }
);

/**
 * Helper for mutating requests that should be safe to retry.
 * Generates a fresh Idempotency-Key (or reuses one passed in `opts.idempotencyKey`).
 */
export const idempotentPost = (url, body, opts = {}) =>
  API.post(url, body, {
    ...opts,
    headers: {
      ...(opts.headers || {}),
      'Idempotency-Key': opts.idempotencyKey || uuid(),
    },
  });

export default API;
